import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { ConfirmProvider } from 'material-ui-confirm';

/***** App Layout Components *****/
import Basic from 'components/Templates/Basic';
import ProtectedRoute from './ProtectedRoute';
import { AuthProvider } from 'contexts/AuthContext';
import { ReactFlowProvider } from 'reactflow';


/***** Pages *****/
const Dashboard = lazy(() => import('./pages/admin/traffic-shape/Dashboard.js'));

const Journeys = lazy(() => import('./pages/Journeys/Journeys.js'));
const PageJourney = lazy(() => import('./pages/Journeys/Journey.js'));
const JourneyMessage = lazy(() => import('./pages/Journeys/JourneyMessage.js'));


const ImportSessionsPage = lazy( () => import('./pages/ImportSessions/ImportSessionsPage.js'));
const ImportSessionConfigure = lazy( () => import('./pages/ImportSessions/ImportSessionConfigure.js'));
const ImportSessionUploadCsv = lazy( () => import('./pages/ImportSessions/ImportSessionUploadCsv.js'));
const ImportSessionStatusPage = lazy( () => import('./pages/ImportSessions/ImportSessionStatusPage.js'));

const Campaigns = lazy(() => import('./pages/Campaigns/Campaigns.js'));
const Settings = lazy(() => import('./pages/Settings/Settings.js'));


const VmtaPoolRates = lazy(() => import('./pages/Settings/vmta-pools/[id]/rates/VmtaPool.js'));
const VmtaPoolVmtas = lazy(() => import('./pages/Settings/vmta-pools/[id]/vmtas/Page.js'));
const VmtaPools = lazy(() => import('./pages/Settings/vmta-pools/Page.js'));

const Library = lazy(() => import('./pages/Library/Library.js'));



const LoginPage = lazy(() => import('./pages/auth/LoginPage.js'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage.js'));
const InvitePage = lazy(() => import('./pages/auth/InvitePage.js'));
const RecoveryPage = lazy(() => import('./pages/auth/RecoveryPage.js'));
const VerificationPage = lazy(() => import('./pages/auth/VerificationPage.js'));
const CreateMyOrganizationPage = lazy(() => import('./pages/auth/CreateOrganizationPage.js'));


const DescendantOrganizationPage = lazy(() => import('./pages/organizer/descendant-organizations/[organization_id]/Page.jsx'));
const DescendantOrganizationsList = lazy(() => import('./pages/organizer/descendant-organizations/OrganizationsList.jsx'));

// the global create organization page

const PrivacyPolicy = lazy(() => import('./pages/Static/Privacy.js'));
const EmailVerifications = lazy(() => import('./pages/public/email-verifications/VerificationPage.jsx'));
const Forms = lazy(() => import('./pages/public/forms/Page.jsx'));
const AdminForms = lazy(() => import('./pages/Forms/Forms.js'));

const VariantStrategy = lazy(() => import('./pages/Campaigns/components/VariantStrategy.js'));

const BatchesPage = lazy(() => import('./pages/Batches/Batches.js'));
const SingleBatchPage = lazy(() => import('./pages/Batches/SingleBatchPage.js'));

const AllAudiencePage = lazy(() => import('./pages/Audiences/Audiences.js'));
const SearchSegment = lazy(() => import('./pages/Profiles/SearchSegment.js'));

const StripeCheckout = lazy(() => import('./pages/billing/StripeCheckout.js'));
const BillingSubscription = lazy(() => import('./pages/billing/BillingSubscription.js'));

const CreateCampaign = lazy(() => import('./pages/Campaigns/PageCampaignEdit.js'));
const ScheduledCampaign = lazy(() => import('./pages/Campaigns/ScheduledCampaign.js'));
const CompletedCampaign = lazy(() => import('./pages/Campaigns/CompletedCampaign.js'));

const AudienceDynamicPage = lazy(() => import('./pages/Audiences/AudienceDynamic.js'));
const AudienceStaticPage = lazy(() => import('./pages/Audiences/AudienceStatic.js'));

const ErrorPage = lazy(() => import('./pages/ErrorPage.js'));
const OnboardingTypeform = lazy(() => import('./pages/onboarding/OnboardingTypeform.js'));

/*** Examples ***/
// const QueryBuilderExample = lazy(() => import('./pages/Examples/QueryBuilderExample.js'));
const UsersAndInvitesPage = lazy(() => import('./pages/admin/users/UsersAndInvitesPage.jsx'));

const EmailRepliesPage = lazy(() => import('./pages/email-replies/Page'));

const PmtaConfigs = lazy(() => import('./pages/admin/mtas/pmta/configs/PmtaConfigs.js'));
const CaddyProxyConfigs = lazy(() => import('./pages/admin/mtas/pmta/configs/CaddyProxyConfigs.js'));
const KumoConfigs = lazy(() => import('./pages/admin/mtas/pmta/configs/KumoConfigs.js'));
const PixelActions = lazy(() => import('./pages/admin/pixel-actions/Page.jsx'));
const PixelActionView = lazy(() => import('./pages/admin/pixel-actions/View.jsx'));

const MyOrganization = lazy(() => import('./pages/Settings/MyOrganization.js'));
const VmtasPage = lazy(() => import('./pages/admin/vmtas/Page.jsx'));
const IpAddressPage = lazy(() => import('./pages/admin/vmtas/[id]/IpAddressPage.jsx'));
const VmtaPage = lazy(() => import('./pages/admin/vmtas/[id]/VmtaPage.jsx'));
const VmtaPoolPage = lazy(() => import('./pages/admin/vmtas/[id]/VmtaPoolPage.jsx'));

const EventQueryBuilderDemo = lazy(() => import('./pages/event-queries/builder-demo/Page.jsx'));
const IntegrationsPage = lazy(() => import('./pages/Integrations/Integrations.js'));

const Root = () => {
    return (
        <Suspense>
            <BrowserRouter basename='/'>
                <AuthProvider>
                <ReactFlowProvider>
                <SnackbarProvider>
                    <Switch>
                        <Redirect exact from='/logout' to='/login'/>

                        <Redirect exact from='/' to='/campaigns'/>


                        <ProtectedRoute exact path='/dashboard/:tab?' component={Dashboard} pageName='Dashboard' name='Dashboard' />

                        <ProtectedRoute exact path='/batches' component={BatchesPage} pageName='Batches' name='Batches' />
                        <ProtectedRoute exact path='/batches/:id' component={SingleBatchPage} pageName='Batch' name='Batch' />

                        <ProtectedRoute exact path='/journeys/:id(\d+)/edit' component={PageJourney} pageName='Journeys' name='CreateJourney' />
                        <ProtectedRoute exact path='/journeys/:id(\d+)/active' component={PageJourney} pageName='Journeys' name='Active Journey' />
                        <ProtectedRoute exact path='/journeys/:tab(\w+)?' component={Journeys} pageName='Journeys'/>
                        <ProtectedRoute exact path='/journeys/:id/message/:stepId/:step' component={JourneyMessage} pageName='Journeys' name='CreateJourney' />

                        <ProtectedRoute exact path='/campaigns/:id/audience-counts' component={lazy(() => import('./pages/Campaigns/CampaignAudiencesCounts'))} pageName='Completed Audiences Counts'/>
                        <ProtectedRoute exact path='/campaigns/:tab?' component={Campaigns} pageName='Campaigns' name='CampaignStatistics' />
                        {/*<ProtectedRoute exact path='/campaigns-completed' component={lazy(() => import('./pages/Campaigns/Completed.js'))} pageName='Completed Campaigns'/>*/}
                        {/*<ProtectedRoute exact path='/campaigns-draft' component={lazy(() => import('./pages/Campaigns/Draft.js'))} pageName='Completed Campaigns'/>*/}
                        <ProtectedRoute exact path='/campaigns/scheduled/:id' component={ScheduledCampaign} pageName='Campaigns' name='EditCampaigns' />
                        <ProtectedRoute exact path='/campaigns/completed/:id' component={CompletedCampaign} pageName='Campaigns' name='Completedcampaign' />
                        <ProtectedRoute exact path='/campaigns/:id/:step?/:substep?' component={CreateCampaign} pageName='Campaigns'/>

                        <ProtectedRoute exact path='/audiences' component={AllAudiencePage} pageName='Audiences' name='SingleAudience' />
                        <ProtectedRoute exact path='/audiences/dynamic/:id' component={AudienceDynamicPage} pageName='Audiences' name='Dynamic Audience' />
                        <ProtectedRoute exact path='/audiences/static/:id' component={AudienceStaticPage} pageName='Audiences' name='Static Audience' />

                        <ProtectedRoute exact path='/profile-props' component={lazy(() => import('./pages/Profiles/Props.js'))} pageName='Profile Properties'/>
                        <ProtectedRoute exact path='/profile-props/create' component={lazy(() => import('./pages/Profiles/CreateProfileProp.js'))} pageName='Create Profile Property'/>

                        <ProtectedRoute exact path='/profiles/events' component={lazy(() => import('./pages/Profiles/Events.js'))} pageName='Events'/>
                        <ProtectedRoute exact path='/profiles/event-types' component={lazy(() => import('./pages/Profiles/EventTypes.js'))} pageName='Event Types'/>

                        <ProtectedRoute exact path='/profiles/query' component={SearchSegment} pageName='Audiences' name='Search and Segment' />

                        <ProtectedRoute exact path='/import-sessions' component={ImportSessionsPage} pageName='Import Sessions' />
                        <ProtectedRoute exact path='/import-sessions/upload' component={ImportSessionUploadCsv} pageName='Import Session Start' />
                        <ProtectedRoute exact path='/import-sessions/:id/configure' component={ImportSessionConfigure} pageName='Import Session Configure' />
                        <ProtectedRoute exact path='/import-sessions/:id/status' component={ImportSessionStatusPage} pageName='Import Session Status' />


                        <ProtectedRoute exact path='/settings/senders' component={lazy(() => import('./pages/Settings/senders/Page.jsx'))} pageName='Senders'/>

                        <ProtectedRoute exact path='/library/:tab?' component={Library} pageName='Library' />


                        <ProtectedRoute exact path='/settings/:tab?' component={Settings} pageName='Settings'/>




                        <ProtectedRoute exact path='/vmta-pools' component={VmtaPools} pageName='Vmta Pools'/>
                        <ProtectedRoute exact path='/vmta-pools/:id/rates' component={VmtaPoolRates} pageName='Vmta Pool Rates'/>
                        <ProtectedRoute exact path='/vmta-pools/:id/vmtas' component={VmtaPoolVmtas} pageName='Vmta Pool VMTAs'/>


                        <ProtectedRoute exact path='/analytics/variant-strategy' component={VariantStrategy} pageName='Variant Strategy'/>


                        <ProtectedRoute exact path='/admin/suppression-lists/optizmo' component={lazy(() => import('./pages/admin/suppression-lists/optizmo/SuppressionOptizmos.js'))} pageName='Optizmo Offers'/>
                        <ProtectedRoute exact path='/admin/suppression-lists/optizmo/create' component={lazy(() => import('./pages/admin/suppression-lists/optizmo/SuppressionOptizmosCreate.jsx'))} pageName='Optizmo Offers - Add'/>
                        <ProtectedRoute exact path='/admin/complaints' component={lazy(() => import('./pages/admin/complaints/Complaints'))} pageName='Complaints'/>
                        <ProtectedRoute exact path='/admin/campaigns/current' component={lazy(() => import('./pages/admin/campaigns/Current'))} pageName='Scheduled / Sending Campaigns' name="AdminActiveCampaigns"/>
                        <ProtectedRoute exact path='/admin/variants/sendable' component={lazy(() => import('./pages/admin/campaigns/Variants'))} pageName='Campaign Variants'/>
                        <ProtectedRoute exact path='/admin/util/reset-sidebar' component={lazy(() => import('./pages/admin/util/ResetSidebar'))} pageName='Reset Sidebar'/>
                        <ProtectedRoute exact path='/admin/util/toolbox' component={lazy(() => import('./pages/admin/util/Toolbox'))} pageName='Toolbox'/>
                        <ProtectedRoute exact path='/admin/ip-addresses' component={lazy(() => import('./pages/admin/ip-addresses/Page'))} pageName='Ip Addresses'/>
                        <ProtectedRoute exact path='/admin/vmtas' component={VmtasPage} pageName='Vmtas'/>
                        <ProtectedRoute exact path='/admin/vmtas/ip-addresses/:id' component={IpAddressPage} pageName='IP Address Details'/>
                        <ProtectedRoute exact path='/admin/vmtas/vmtas/:id' component={VmtaPage} pageName='VMTA Details'/>
                        <ProtectedRoute exact path='/admin/vmtas/vmta-pools/:id' component={VmtaPoolPage} pageName='VMTA Pool Details'/>
                        <ProtectedRoute exact path='/admin/vmtas/vmta-pools/:id/vmtas' component={lazy(() => import('./pages/admin/vmtas/[id]/VmtaPoolVmtasPage'))} pageName='VMTA Pool VMTAs'/>
                        <ProtectedRoute exact path='/admin/vmtas/vmta-pools/:id/rates' component={lazy(() => import('./pages/admin/vmtas/[id]/VmtaPoolRatesPage'))} pageName='VMTA Pool Rates'/>
                        <ProtectedRoute exact path='/admin/pmta/configs' component={PmtaConfigs} pageName='PMTA Configs'/>
                        <ProtectedRoute exact path='/admin/caddy/configs' component={CaddyProxyConfigs} pageName='Caddy Configs'/>
                        <ProtectedRoute exact path='/admin/kumo/configs' component={KumoConfigs} pageName='Kumo Configs'/>
                        <ProtectedRoute exact path='/admin/config' component={lazy(() => import('./pages/admin/config/Page'))} pageName='Configs'/>
                        <ProtectedRoute exact path='/admin/organizers' component={lazy(() => import('./pages/admin/organizers/Page'))} pageName='Admin Organizers' />
                        <ProtectedRoute exact path='/admin/organizations' component={lazy(() => import('./pages/admin/organizations/Page'))} pageName='Admin Organizations' />
                        <ProtectedRoute exact path='/admin/email-sending-domains' component={lazy(() => import('./pages/admin/email-sending-domains/Page'))} pageName='Email Sending Domains' />
                        <ProtectedRoute exact path='/admin/email-sending-domains/:id' component={lazy(() => import('./pages/admin/email-sending-domains/[id]/Page'))} pageName='Email Sending Domain Details' />
                        <ProtectedRoute exact path='/admin/email-sender-profiles' component={lazy(() => import('./pages/admin/email-sender-profiles/Page'))} pageName='Admin Email Sender Profiles' />
                        <ProtectedRoute exact path='/admin/outgoing-email-services' component={lazy(() => import('./pages/admin/outgoing-email-services/Page'))} pageName='Admin Outgoing Email Services' />
                        <ProtectedRoute exact path='/admin/organizations/:id' component={lazy(() => import('./pages/admin/organizations/[id]/Page'))} pageName='Organization Details' />
                        <ProtectedRoute exact path='/admin/pixel-actions' component={PixelActions} pageName='Pixel Actions' />
                        <ProtectedRoute exact path='/admin/pixel-actions/:id' component={PixelActionView} pageName='Pixel Action Details' />
                        <ProtectedRoute exact path='/admin/delivery-overview' component={lazy(() => import('./pages/admin/delivery-overview/Page'))} pageName='Delivery Overview' />
                        <ProtectedRoute exact path='/admin/audience-snapshots' component={lazy(() => import('./pages/admin/audience-snapshots/Page'))} pageName='Audience Snapshots' />
                        <ProtectedRoute exact path='/admin/debug/campaign-counts' component={lazy(() => import('./pages/admin/debug/CampaignCounts'))} pageName='Campaign Counts Debug' />
                        <ProtectedRoute exact path='/forms' component={AdminForms} pageName='Forms' />
                        <ProtectedRoute exact path='/organizer/descendant-organizations' component={DescendantOrganizationsList} pageName='Organizations' />
                        <ProtectedRoute exact path='/organizer/descendant-organizations/create' component={lazy(() => import('./pages/organizer/descendant-organizations/CreateOrganizationPage'))} pageName='Create Organization' />
                        <ProtectedRoute exact path='/organizer/descendant-organizations/:organization_id' component={DescendantOrganizationPage} pageName='Organization Details' />
                        <ProtectedRoute exact path='/organizer/config' component={lazy(() => import('./pages/organizer/config/Page'))} pageName='Organizer Configuration' />
                        <ProtectedRoute exact path='/my-organization' component={MyOrganization} pageName='My Organization' />

                        <ProtectedRoute exact path='/upload-process-sessions' component={lazy(() => import('./pages/UploadProcessSessions/UploadProcessSessions.js'))} pageName='Upload Sessions'/>
                        <ProtectedRoute exact path='/upload-process-sessions/suppressions/upload' component={lazy(() => import('./pages/UploadProcessSessions/SuppressionUpload.js'))} pageName='Suppression Upload'/>

                        <ProtectedRoute exact path='/upload-process-sessions/:id' component={lazy(() => import('./pages/UploadProcessSessions/UploadProcessSession.js'))} pageName='Upload Session'/>



                        {/*<ProtectedRoute exact path='/billing/checkout' component={StripeCheckout} pageName='Checkout'/>*/}
                        {/*<ProtectedRoute exact path='/billing/subscription' component={BillingSubscription} pageName='Checkout'/>*/}

                        <ProtectedRoute exact path='/register/onboarding' component={OnboardingTypeform} pageName='OnboardingTypeform' />
                        <ProtectedRoute exact path='/onboarding' component={lazy(() => import('./pages/onboarding/OnboardingChecklist'))}  pageName='OnboardingTypeform' />

                        <ProtectedRoute path='/user-settings' component={lazy(() => import('./pages/auth/SettingsPage'))} />

                        <ProtectedRoute path='/verification' component={VerificationPage} />

                        <ProtectedRoute path='/kumomta' component={lazy(() => import('./pages/kumomta/PageKumoMTA'))} />

                        <ProtectedRoute path="/users/me" component={lazy(() => import('./pages/admin/users/MePage'))} />
                        <ProtectedRoute path="/users" component={lazy(() => import('./pages/admin/users/UsersAndInvitesPage'))} />


                        <ProtectedRoute
                            exact
                            path='/email-replies'
                            component={EmailRepliesPage}
                            pageName='Email Replies'
                        />

                        <ProtectedRoute
                            exact
                            path='/event-queries'
                            component={EventQueryBuilderDemo}
                            pageName='Event Queries'
                        />

                        <ProtectedRoute
                            exact
                            path='/integrations'
                            component={IntegrationsPage}
                            pageName='Integrations'
                        />

                        <Route path='/register' render={props => <Basic main={<RegisterPage/>} {...props}/>}/>


                        <Route path='/login' render={props => <Basic main={<LoginPage/>} {...props}/>}/>
                        <Route path='/recovery' render={props => <Basic main={<RecoveryPage/>} {...props}/>}/>

                        // TODO: Should be protected
                        <Route path='/create-organization' render={props => <Basic main={<CreateMyOrganizationPage />} {...props}/>}/>

                        <Route path='/privacy-policy' render={props => <Basic main={ <PrivacyPolicy />} {...props}/>} />

                        <Route path='/email-verifications' render={props => <Basic main={ <EmailVerifications />} {...props}/>} />

                        <Route path="/invite" render={props => <Basic main={ <InvitePage />} {...props}/>} />


                        <Route path="/forms/:id/confirm/:code" render={props => <Basic main={ <Forms />} {...props}/>} />
                        <Route path="/forms/:id" render={props => <Basic main={ <Forms />} {...props}/>} />







                        <ProtectedRoute component={ErrorPage} />


                        
                    </Switch>
                </SnackbarProvider>
                </ReactFlowProvider>
                </AuthProvider>
            </BrowserRouter>
        </Suspense>
    );
};

export default Root;
